// Admin auth context. Stores JWT + admin user in localStorage and
// hydrates on app start. Same pattern as bounce-dads.
import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import type { AuthState, AdminUser } from '../types';

const DEBUG = process.env.REACT_APP_DEBUG_LOGGING === 'true';
const debugLog = (msg: string) => { if (DEBUG) console.log(`[auth] ${msg}`); };

interface AuthContextType {
  state: AuthState;
  loading: boolean;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { token: string; user: AdminUser } }
  | { type: 'LOGOUT' }
  | { type: 'INIT_AUTH'; payload: AuthState };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return { isAuthenticated: true, token: action.payload.token, user: action.payload.user };
    case 'LOGOUT':
      return { isAuthenticated: false };
    case 'INIT_AUTH':
      return action.payload;
    default:
      return state;
  }
};

const initialState: AuthState = { isAuthenticated: false };

const validateToken = (token: string): boolean => {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    // 5-min buffer so we don't surf the expiry edge
    return !!(payload.exp && payload.exp > now + 300);
  } catch (e) {
    debugLog(`Token validation failed: ${e}`);
    return false;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('authUser');
    if (token && userStr) {
      if (validateToken(token)) {
        dispatch({
          type: 'INIT_AUTH',
          payload: { isAuthenticated: true, token, user: JSON.parse(userStr) },
        });
        debugLog('Auth restored from localStorage');
      } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        debugLog('Stale token cleared');
      }
    }
    setLoading(false);
  }, []);

  const login = (token: string, user: AdminUser) => {
    if (!validateToken(token)) {
      throw new Error('Invalid authentication token');
    }
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(user));
    dispatch({ type: 'LOGIN_SUCCESS', payload: { token, user } });
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider value={{ state, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
